import type { AgendaSlotDto } from "~/ui-rtk/api/agendaApi"
import { formatNumber } from "~/ui-rtk/utils/format"

/** "Ertuğrul Göktaş" → "EG"; single names give one letter; empty → "?". */
export function initials(name?: string | null): string {
  if (!name) return "?"
  return (
    name
      .split(/\s+/)
      .map(part => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  )
}

/**
 * Progress toward a slot's quantity, derived from its links. Mirrors the
 * API's recompute rule: AD_CREATIVE slots count only APPROVED units (delivered
 * work still needs review); every other type counts DELIVERED too. Returns
 * null for slots without a target quantity.
 */
export function slotProgress(
  slot: AgendaSlotDto,
): { completed: number; target: number } | null {
  if (!slot.quantity) return null
  const counted =
    slot.type === "AD_CREATIVE" ? ["APPROVED"] : ["DELIVERED", "APPROVED"]
  const completed = (slot.links ?? [])
    .filter(link => counted.includes(link.status))
    .reduce((sum, link) => sum + link.units, 0)
  return { completed, target: slot.quantity }
}

/** Untyped per-entity row of an audit — the shape varies per audit type. */
export type AuditItem = Record<string, unknown>

const firstString = (...values: unknown[]) =>
  values.find(
    (value): value is string => typeof value === "string" && value.length > 0,
  )

export function itemName(item: AuditItem, index: number): string {
  return (
    firstString(
      item.entityName,
      item.name,
      item.adSetName,
      item.campaignName,
      item.title,
    ) ??
    firstString(item.entityId, item.adSetId, item.adId, item.id) ??
    `Item ${index + 1}`
  )
}

/** Budgets in audit items are stored in cents. */
const cents = (value: unknown) =>
  typeof value === "number"
    ? formatNumber(value / 100, { currency: true })
    : undefined

/** Item keys already surfaced as the row name / outcome, hidden from details. */
const NAME_KEYS = new Set([
  "entityName",
  "name",
  "adSetName",
  "campaignName",
  "title",
  "entityId",
  "adSetId",
  "adId",
  "id",
  "success",
  "error",
])

/** One-line summary of what an audit item changed, keyed by audit type. */
export function describeItem(type: string, item: AuditItem): string {
  if (type === "status-update") {
    // `previousStatus` is absent on rows written before APP-513 and on
    // never-synced entities — fall back to the after-only form.
    const transition =
      item.previousStatus != null
        ? `${item.previousStatus} → ${item.status}`
        : item.status
    return [item.entity, transition].filter(Boolean).join(" → ")
  }
  if (type === "budget-update" || type === "budget-recommendation") {
    const parts: string[] = []
    if (item.action === "pause") parts.push("Paused")
    // Recommender rows carry the transition on `previousDailyBudget` →
    // `newDailyBudget`. Direct-path rows never set `newDailyBudget` — they
    // carry the platform DTO's own fields, each with a `previous*` twin — so
    // this must not fire for them or a plain ad set edit reads "$50 → —".
    if (type === "budget-recommendation") {
      // Explicit-unit names since MD-5572; older rows carry the same cents
      // under the unlabeled names.
      const previous = item.previousDailyBudgetCents ?? item.previousDailyBudget
      const next = item.newDailyBudgetCents ?? item.newDailyBudget
      if (previous != null || next != null) {
        parts.push(`${cents(previous) ?? "—"} → ${cents(next) ?? "—"}`)
      }
    } else {
      // `previous*` is absent on rows written before APP-513 and on
      // never-synced entities — fall back to the after-only form.
      const pair = (previous: unknown, next: unknown) =>
        previous != null
          ? `${cents(previous) ?? "—"} → ${cents(next)}`
          : cents(next)
      if (cents(item.dailyBudget)) {
        parts.push(`daily ${pair(item.previousDailyBudget, item.dailyBudget)}`)
      }
      if (cents(item.lifetimeBudget)) {
        parts.push(
          `lifetime ${pair(item.previousLifetimeBudget, item.lifetimeBudget)}`,
        )
      }
      if (cents(item.budgetAmount)) {
        parts.push(
          `budget ${pair(item.previousBudgetAmount, item.budgetAmount)}`,
        )
      }
    }
    if (typeof item.channel === "string") parts.push(item.channel)
    if (parts.length > 0) return parts.join(" · ")
  }
  // Generic fallback: the item's scalar fields.
  return Object.entries(item)
    .filter(
      ([key, value]) =>
        !NAME_KEYS.has(key) &&
        (typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean"),
    )
    .slice(0, 4)
    .map(([key, value]) => `${key}: ${value}`)
    .join(" · ")
}
